"use client";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { ValidationResult } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { Size } from "../../utils/controlStyles";
import { Chip } from "../Chip";
import {
  BaseInputProps,
  Description,
  FieldError,
  getInputBaseStyles,
  InputWrapper,
  Label,
  useInputFocus,
} from "../Field";

export interface ChipData {
  id: string;
  label: string;
  value: unknown;
}

interface TriggerInfo {
  query: string;
  position: number;
  startIndex: number;
}

export interface TextAreaWithChipsProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size" | "value" | "onChange" | "disabled">,
    BaseInputProps {
  // Standard TextArea props (consistent with TextArea.tsx)
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  tooltip?: string;
  size?: Size;
  isInvalid?: boolean;
  isDisabled?: boolean;
  /**
   * Allow user resizing of the content area. Defaults to false (non-resizable).
   * When true, enables vertical resize.
   */
  isResizable?: boolean;
  validationResult?: ValidationResult;

  // Text content - using standard 'value' and 'onChange' names for consistency
  value?: string;
  onChange?: (value: string) => void;

  // Chip functionality
  chips?: ChipData[];
  onChipsChange?: (chips: ChipData[]) => void;

  // Trigger detection
  triggerPattern?: string | RegExp;
  onTriggerDetected?: (triggerInfo: TriggerInfo) => void;
  onTriggerDismissed?: () => void;

  // Chip rendering customization
  renderChip?: (chip: ChipData, onRemove: () => void) => React.ReactNode;
  chipVariant?: "primary" | "secondary" | "default" | "brand";
  chipSize?: Size;
}

/**
 * TextAreaWithChips
 *
 * Enhanced textarea component with chip/tag support.
 * Enables rich text input with embedded chips for mentions, tags, or structured data entry.
 */
export function TextAreaWithChips({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  required,
  transparent,
  isInvalid,
  isDisabled,
  isResizable = false,
  className,
  validationResult,

  // Text props (consistent naming with standard TextArea)
  value = "",
  onChange,

  // Chip props
  chips = [],
  onChipsChange,
  triggerPattern = "{{",
  onTriggerDetected,
  onTriggerDismissed,
  renderChip,
  chipVariant = "primary",
  chipSize,

  ...props
}: TextAreaWithChipsProps) {
  const textareaRef = useRef<HTMLDivElement>(null);
  const { isFocused, handleFocus, handleBlur } = useInputFocus();
  // Use smaller chip sizes that match text line height better
  const actualChipSize = chipSize || "sm";

  // Track desired caret offset across re-renders to preserve cursor position
  const selectionOffsetRef = useRef<number | null>(null);

  // Helper: convert DOM content back to text with chip placeholders
  const getDOMTextWithPlaceholders = useCallback((container: HTMLDivElement): string => {
    let result = "";

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Only include text nodes that are not inside chip elements
        const parentElement = node.parentElement;
        if (!parentElement?.closest("[data-chip-id]")) {
          const textContent = node.textContent || "";
          result += textContent;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;

        if (element.hasAttribute("data-chip-id")) {
          // Replace chip with placeholder
          const chipId = element.getAttribute("data-chip-id");
          result += `{{${chipId}}}`;
          // Don't process children of chip elements
          return;
        } else if (element.tagName === "BR") {
          result += "\n";
          return;
        } else if (element.tagName === "DIV") {
          // Handle div elements that might be created by contentEditable
          // Process children but don't add extra content
        }
      }

      // Process child nodes
      for (let i = 0; i < node.childNodes.length; i++) {
        processNode(node.childNodes[i]);
      }
    };

    processNode(container);

    // Clean up any extra whitespace that might have been introduced
    // but preserve intentional spaces and line breaks
    return result;
  }, []);

  // Helper: set caret at given text offset within the contentEditable container
  const setCaretAtOffset = useCallback((container: HTMLDivElement, targetOffset: number) => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Skip text within chip elements (contentEditable=false)
        const parentElement = node.parentElement;
        if (parentElement && parentElement.closest("[data-chip-id]")) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let currentNode: Node | null = walker.nextNode();
    let remaining = Math.max(0, targetOffset);

    while (currentNode) {
      const text = currentNode.nodeValue || "";
      if (text.length >= remaining) {
        const range = document.createRange();
        range.setStart(currentNode, remaining);
        range.collapse(true);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        return;
      }
      remaining -= text.length;
      currentNode = walker.nextNode();
    }

    // Fallback: place at end
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  // Handle empty state properly to avoid DOM conflicts
  useEffect(() => {
    if (textareaRef.current && !value) {
      // Only clear if there's actual content to avoid DOM manipulation conflicts
      if (textareaRef.current.textContent !== "") {
        textareaRef.current.textContent = "";
      }
    }
  }, [value]);

  // After value changes, restore caret to the last known offset
  useLayoutEffect(() => {
    if (!textareaRef.current) return;
    if (value === "") {
      // If empty and focused, ensure focus and caret at start so user can type immediately
      if (isFocused) {
        try {
          textareaRef.current.focus({
            preventScroll: true,
          } as unknown as FocusOptions);
          setCaretAtOffset(textareaRef.current, 0);
        } catch (_) {
          // ignore
        }
      }
      return;
    }
    if (selectionOffsetRef.current == null) return;
    try {
      setCaretAtOffset(textareaRef.current, selectionOffsetRef.current);
    } finally {
      selectionOffsetRef.current = null;
    }
  }, [value, setCaretAtOffset, isFocused]);

  // Handle text changes and trigger detection for contentEditable
  const handleTextChange = useCallback(
    (newValue: string, cursorPosition?: number) => {
      onChange?.(newValue);

      // If text is empty, dismiss any active triggers
      if (newValue === "") {
        onTriggerDismissed?.();
        return;
      }

      // Check for trigger pattern
      if (triggerPattern && onTriggerDetected && cursorPosition !== undefined) {
        const textBeforeCursor = newValue.slice(0, cursorPosition);

        const triggerRegex =
          typeof triggerPattern === "string" ? new RegExp(`\\${triggerPattern}([^\\s]*)$`) : triggerPattern;

        const match = textBeforeCursor.match(triggerRegex);

        if (match) {
          const query = match[1] || "";
          const startIndex = cursorPosition - match[0].length;

          onTriggerDetected({
            query,
            position: cursorPosition,
            startIndex,
          });
        } else {
          onTriggerDismissed?.();
        }
      }
    },
    [onChange, triggerPattern, onTriggerDetected, onTriggerDismissed]
  );

  // Handle chip removal
  const handleChipRemove = useCallback(
    (chipId: string) => {
      if (!onChipsChange || !onChange) return;

      const updatedChips = chips.filter((chip) => chip.id !== chipId);
      onChipsChange(updatedChips);

      // Also remove from text content
      const updatedText = value.replace(new RegExp(`\\{\\{${chipId}\\}\\}`, "g"), "");
      onChange(updatedText);
    },
    [chips, onChipsChange, value, onChange]
  );

  // Render chip or use default rendering
  const renderChipElement = useCallback(
    (chip: ChipData) => {
      if (renderChip) {
        return renderChip(chip, () => handleChipRemove(chip.id));
      }

      return (
        <Chip
          key={chip.id}
          variant={chipVariant}
          size={actualChipSize}
          onRemove={isDisabled ? undefined : () => handleChipRemove(chip.id)}
          isDisabled={isDisabled}
          className="py-0.5 px-1.5 gap-1 text-xs leading-none rounded-md"
        >
          {chip.label}
        </Chip>
      );
    },
    [renderChip, chipVariant, actualChipSize, handleChipRemove, isDisabled]
  );

  // Get textarea styles - using same approach as TextField
  const getTextAreaStyles = () => {
    // Get base input styles (same as TextField)
    const baseStyles = getInputBaseStyles({
      isInvalid,
      isDisabled,
      isFocused,
      transparent,
      size,
    });

    // Add TextArea-specific styles
    const textAreaStyles = `min-h-[100px] py-2 px-3 ${
      isDisabled ? "resize-none" : isResizable ? "resize-y" : "resize-none"
    }`;

    // ContentEditable and chip-specific styles
    const contentEditableStyles = twMerge(
      "focus:outline-none overflow-auto",

      // Chip selection styles
      "[&_[data-chip-id]::selection]:bg-action-primary/20",

      // Placeholder styles for contentEditable - only show when completely empty
      "[&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-text-disabled [&:empty]:before:pointer-events-none"
    );

    return twMerge(baseStyles, textAreaStyles, contentEditableStyles, className);
  };

  // Render content with inline chips
  const renderContent = () => {
    // If no text value, return nothing (let CSS placeholder handle it)
    if (!value) {
      return null;
    }

    const parts = value.split(/(\{\{[^}]+\}\})/g);

    return parts.map((part, index) => {
      // Check if this part is a chip placeholder
      const chipMatch = part.match(/\{\{([^}]+)\}\}/);
      if (chipMatch) {
        const chipId = chipMatch[1];
        const chip = chips.find((c) => c.id === chipId);
        if (chip) {
          return (
            <span
              key={`chip-${chipId}-${index}`}
              className="inline-block align-baseline cursor-pointer"
              contentEditable={false}
              suppressContentEditableWarning
              data-chip-id={chipId}
              style={{
                userSelect: "none",
                verticalAlign: "baseline",
                lineHeight: "1",
              }}
              onClick={(e) => {
                e.preventDefault();
                // Select the chip element
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNode(e.currentTarget);
                selection?.removeAllRanges();
                selection?.addRange(range);
              }}
            >
              {renderChipElement(chip)}
            </span>
          );
        }
      }

      // Regular text - preserve line breaks
      if (part.includes("\n")) {
        return part.split("\n").map((line, lineIndex) => (
          <React.Fragment key={`line-${index}-${lineIndex}`}>
            {line}
            {lineIndex < part.split("\n").length - 1 && <br />}
          </React.Fragment>
        ));
      }

      return part;
    });
  };

  return (
    <div className="group flex flex-col">
      <div className="flex flex-col">
        {label && (
          <Label size={size} tooltip={tooltip} isRequired={required}>
            {label}
          </Label>
        )}

        <InputWrapper>
          <div
            // Force a safe remount when the content becomes fully empty to avoid
            // React diffing contentEditable internals during browser normalization
            key={value === "" ? "empty" : "content"}
            ref={textareaRef}
            contentEditable={!isDisabled}
            suppressContentEditableWarning
            onInput={(e) => {
              if (isDisabled) return;
              const target = e.target as HTMLDivElement;

              // Check if there are any chip elements in the DOM
              const hasChips = target.querySelector("[data-chip-id]") !== null;

              let newValue: string;

              if (hasChips) {
                // Only use complex DOM parsing when chips are present
                newValue = getDOMTextWithPlaceholders(target);
                // Normalize runaway spaces that can accumulate adjacent to chips
                newValue = newValue
                  // collapse 2+ spaces AFTER a chip to a single space
                  .replace(/(\{\{[^}]+\}\})\s{2,}/g, "$1 ")
                  // collapse 2+ spaces BEFORE a chip to a single space
                  .replace(/\s{2,}(\{\{[^}]+\}\})/g, " $1");
              } else {
                // Use simple textContent for plain text to avoid issues
                newValue = target.textContent || "";
              }

              // If cleared, short-circuit to avoid selection/range work during DOM teardown
              if (newValue === "") {
                // Place caret at start and keep focus so user can continue typing
                try {
                  const range = document.createRange();
                  range.selectNodeContents(target);
                  range.collapse(true);
                  const sel = window.getSelection();
                  sel?.removeAllRanges();
                  sel?.addRange(range);
                  selectionOffsetRef.current = 0;
                } catch (_) {
                  // ignore
                }
                handleTextChange("", 0);
                return;
              }

              // Only update if the value actually changed to avoid unnecessary re-renders
              if (newValue === value) {
                return;
              }

              // Get cursor position based on text with placeholders
              const selection = window.getSelection();
              let cursorPosition = 0;

              if (selection && selection.rangeCount > 0) {
                try {
                  const range = selection.getRangeAt(0);

                  if (hasChips) {
                    // Use complex calculation when chips are present
                    let position = 0;
                    let found = false;

                    const calculatePosition = (node: Node): boolean => {
                      if (found) return true;

                      if (node === range.startContainer) {
                        position += range.startOffset;
                        found = true;
                        return true;
                      }

                      if (node.nodeType === Node.TEXT_NODE) {
                        // Only count text nodes that are not inside chip elements
                        const parentElement = node.parentElement;
                        if (!parentElement?.closest("[data-chip-id]")) {
                          if (node.contains(range.startContainer)) {
                            position += range.startOffset;
                            found = true;
                            return true;
                          }
                          position += (node.textContent || "").length;
                        }
                      } else if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as Element;

                        if (element.hasAttribute("data-chip-id")) {
                          // Check if cursor is within this chip element
                          if (element.contains(range.startContainer) || element === range.startContainer) {
                            const chipId = element.getAttribute("data-chip-id");
                            position += `{{${chipId}}}`.length;
                            found = true;
                            return true;
                          }
                          const chipId = element.getAttribute("data-chip-id");
                          position += `{{${chipId}}}`.length;
                          return false; // Don't process children
                        } else if (element.tagName === "BR") {
                          if (element === range.startContainer) {
                            found = true;
                            return true;
                          }
                          position += 1; // for \n
                          return false;
                        }
                      }

                      // Process child nodes
                      for (let i = 0; i < node.childNodes.length; i++) {
                        if (calculatePosition(node.childNodes[i])) {
                          return true;
                        }
                      }

                      return false;
                    };

                    calculatePosition(target);
                    cursorPosition = position;
                  } else {
                    // Use simple calculation for plain text
                    const tempRange = document.createRange();
                    tempRange.setStart(target, 0);
                    tempRange.setEnd(range.startContainer, range.startOffset);
                    cursorPosition = tempRange.toString().length;
                  }
                } catch (_error) {
                  // Fallback to end of content if range creation fails
                  cursorPosition = newValue.length;
                }
              }

              // Persist caret position so we can restore it after React re-renders
              selectionOffsetRef.current = cursorPosition;
              handleTextChange(newValue, cursorPosition);
            }}
            onKeyDown={(e) => {
              if (isDisabled) return;
              const selection = window.getSelection();
              if (!selection || selection.rangeCount === 0) return;

              try {
                const range = selection.getRangeAt(0);
                const container = range.commonAncestorContainer;

                // Handle arrow key navigation around chips
                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                  const chipElement =
                    container.nodeType === Node.TEXT_NODE
                      ? container.parentElement?.closest("[data-chip-id]")
                      : (container as Element).closest("[data-chip-id]");

                  if (chipElement) {
                    e.preventDefault();

                    try {
                      // Move cursor to before or after the chip
                      const newRange = document.createRange();
                      const newSelection = window.getSelection();

                      if (e.key === "ArrowLeft") {
                        newRange.setStartBefore(chipElement);
                      } else {
                        newRange.setStartAfter(chipElement);
                      }

                      newRange.collapse(true);
                      newSelection?.removeAllRanges();
                      newSelection?.addRange(newRange);
                    } catch (_rangeError) {
                      // Ignore range errors - let default behavior happen
                    }
                  }
                }

                // Handle backspace on chips
                if (e.key === "Backspace") {
                  // Identify a chip either selected, or immediately before the caret
                  let chipElement: Element | null = null;

                  // Case 1: caret is inside text or element inside a chip (selected chip)
                  chipElement =
                    (container.nodeType === Node.TEXT_NODE
                      ? container.parentElement?.closest("[data-chip-id]")
                      : (container as Element).closest("[data-chip-id]")) || null;

                  // Case 2: caret is immediately after a chip (most common backspace case)
                  if (!chipElement) {
                    if (container.nodeType === Node.TEXT_NODE) {
                      const textNode = container as Text;
                      if (range.startOffset === 0 && textNode.parentElement) {
                        const prev = textNode.previousSibling;
                        if (prev && prev.nodeType === Node.ELEMENT_NODE) {
                          const maybeChip = (prev as Element).closest("[data-chip-id]");
                          if (maybeChip) chipElement = maybeChip as Element;
                        }
                      }
                    } else {
                      const elementNode = container as Element;
                      if (range.startOffset > 0) {
                        const prev = elementNode.childNodes[range.startOffset - 1];
                        if (prev && prev.nodeType === Node.ELEMENT_NODE) {
                          const maybeChip = (prev as Element).closest("[data-chip-id]");
                          if (maybeChip) chipElement = maybeChip as Element;
                        }
                      }
                    }
                  }

                  if (chipElement) {
                    e.preventDefault();
                    e.stopPropagation();

                    const chipId = chipElement.getAttribute("data-chip-id");
                    if (!chipId) return;

                    // Find the position of this chip in the text
                    const chipPlaceholder = `{{${chipId}}}`;
                    const chipIndex = value.indexOf(chipPlaceholder);

                    if (chipIndex !== -1) {
                      // Remove the chip placeholder from the text
                      const newText = value.slice(0, chipIndex) + value.slice(chipIndex + chipPlaceholder.length);

                      // Update chips array
                      const newChips = chips.filter((c) => c.id !== chipId);

                      // Set caret position to where the chip was
                      selectionOffsetRef.current = chipIndex;

                      // Clear any live selection ranges that may reference the chip DOM node
                      try {
                        const sel = window.getSelection();
                        sel?.removeAllRanges();
                      } catch (_) {
                        // ignore
                      }

                      // Update state directly without any DOM manipulation
                      onChipsChange?.(newChips);
                      onChange?.(newText);
                    }
                  }
                }
              } catch (_error) {
                // Ignore selection/range errors and let default behavior happen
              }
            }}
            onFocus={(e) => {
              if (isDisabled) return;
              if (props.onFocus) {
                props.onFocus(e as unknown as React.FocusEvent<HTMLTextAreaElement>);
              }
              handleFocus(e as unknown as React.FocusEvent<HTMLInputElement>);
            }}
            onBlur={(e) => {
              if (isDisabled) return;
              if (props.onBlur) {
                props.onBlur(e as unknown as React.FocusEvent<HTMLTextAreaElement>);
              }
              handleBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
            }}
            className={getTextAreaStyles()}
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              ...props.style,
            }}
            data-placeholder={props.placeholder}
          >
            {renderContent()}
          </div>
        </InputWrapper>

        {description && (
          <Description size={size} className="mt-3">
            {description}
          </Description>
        )}

        {typeof errorMessage === "string" && (
          <FieldError size={size} className="mt-1">
            {errorMessage}
          </FieldError>
        )}

        {typeof errorMessage === "function" && validationResult && (
          <FieldError size={size} className="mt-1">
            {errorMessage(validationResult)}
          </FieldError>
        )}
      </div>
    </div>
  );
}
